import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from "@qw/ui";
import { useState } from "react";

interface PlaybookPickerPlaybook {
  id: string;
  name?: string | null;
  description?: string | null;
}

interface PlaybookPickerProps {
  playbooks: PlaybookPickerPlaybook[];
  value: string | undefined;
  onChange: (id: string) => void;
  placeholder?: string;
}

export const PlaybookPicker = ({
  playbooks,
  value,
  onChange,
  placeholder = "Select a playbook...",
}: PlaybookPickerProps) => {
  const [open, setOpen] = useState(false);
  const selectedPlaybook = playbooks.find((playbook) => playbook.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" type="button" className="w-full bg-background justify-between font-normal">
          <span className={selectedPlaybook ? "" : "text-muted-foreground"}>
            {selectedPlaybook?.name || placeholder}
          </span>
          <Icon icon="keyboard_arrow_down" className="size-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" collisionPadding={8}>
        {playbooks.length === 0 ? (
          <div className="py-2 px-3 text-sm text-muted-foreground">
            No playbooks available. Create a playbook first.
          </div>
        ) : (
          /* The bound goes on `Command`, not `CommandList`: the search row sits above the list, so
             capping only the list lets that row push the popover past the space Radix measured —
             off-screen, on a trigger low enough that the popover flips upward. `Command` is already
             a `flex flex-col overflow-hidden`; `min-h-0` is what lets the list absorb the shrink. */
          <Command style={{ maxHeight: "min(28rem, var(--radix-popover-content-available-height))" }}>
            <CommandInput placeholder="Search playbooks..." />
            <Separator />
            <CommandList className="min-h-0">
              <CommandEmpty>No matching playbooks.</CommandEmpty>
              {playbooks.map((playbook) => (
                <CommandItem
                  key={playbook.id}
                  value={playbook.id}
                  keywords={[playbook.name ?? "", playbook.description ?? ""]}
                  onSelect={() => {
                    onChange(playbook.id);
                    setOpen(false);
                  }}
                >
                  <div className="flex flex-col">
                    <span>{playbook.name || "(untitled)"}</span>
                    {playbook.description && (
                      <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                        {playbook.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};
